// Customer-facing delivery onboarding API (/api/delivery/me/*). Auth is the httpOnly
// bridge_club_access_token cookie, so every call uses credentials: 'include' and no
// Authorization header. Mirrors the shapes in delivery/onboarding_views.py.
import { API_BASE_URL } from './api';

const BASE = `${API_BASE_URL}/delivery/me`;
// Public (no cookie): the registration flow has no member yet.
const PUBLIC = `${API_BASE_URL}/delivery`;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

const handle = async (res: Response) => {
  if (!res.ok) throw await res.json().catch(() => ({ detail: 'Request failed' }));
  return res.status === 204 ? null : res.json();
};

export interface FeedInfo {
  price: string | null; bag_lbs: string | null; bag_name: string | null; deliverable: boolean;
  blurb: string; image_url: string; nutrition_label_url: string; info_url: string;
  ingredients: string; attributes: Record<string, string[]>;
  // Strict by form: `forms` lists every form with a priced bag; when a form was asked
  // for, `form_available` says whether THAT form has one (and `price` is its price).
  forms: Record<string, { price: string; bag_lbs: string | null; bag_name: string }>;
  form_available: boolean;
  // A seasonal ration (summer + winter layer) is ONE thing to the customer: rows that
  // share ration_key collapse onto the in-season one, named ration_name.
  seasonal: boolean; in_season: boolean; ration_key: string | null; ration_name: string;
}

export interface ScannedProduct {
  id: number;
  brand: string;
  display_name: string;
  // The distributor portal's full name, when the asset import has run.
  full_name?: string;
  // "More about this feed": the portal's ingredient list and attributes, when pulled.
  ingredients?: string;
  attributes?: Record<string, string[]>;
  species: string;
  weight_lbs: string | null;
  upc: string | null;
  unit_retail: string;
  image_url: string | null;
  store_product_id: number | null;
}

export interface ScanResult {
  found: boolean;
  barcode: string;
  product?: ScannedProduct;
  // Miss only. A miss is 200 with a next step, never a bare 404 - see
  // delivery/catalog_scan_views.py.
  normalized?: string;
  message?: string;
  brand_hint?: string[];
  suggestions?: ScannedProduct[];
  can_report?: boolean;
}

export interface OnboardingState {
  status: string;
  steps: Record<string, boolean>;
  next_step: string | null;
  // Customer-facing wording for next_step, served by the API rather than
  // re-listed here - a second copy of the labels would drift from
  // onboarding_views.STEP_LABELS the first time one is reworded.
  next_step_label: string | null;
  complete: boolean;
  property_capture_skipped: boolean;
}

export interface BinPrice {
  bin_id: number;
  name: string;
  feed_category: string;
  deliverable: boolean;
  // Why not, in the customer's words, from the same server branch the admin
  // screen reads. Null when the bin IS deliverable.
  not_deliverable_reason: string | null;
  not_deliverable_code: string | null;
  selected: boolean;
  store_product_id: number | null;
  product_name: string | null;
  size_lbs: string | null;
  unit_price: string | null;
  tax: string | null;
  total: string | null;
  // A bag Red Bridge mills, as opposed to resold pet food or another brand. Every
  // delivery carries at least one milled bag.
  milled: boolean;
}

export interface BinsPricing {
  location: { id: number; name: string; tax_rate: string } | null;
  location_configured: boolean;
  bins: BinPrice[];
  // The soft no (Brad, 2026-09-24): whether this customer asked to be told if pet-food-only
  // delivery becomes possible, and whether a manager allowed it for them.
  pet_only?: { interest_at: string | null; allowed: boolean };
}

export interface SavedCard {
  id: number;
  payment_type: string;
  last_four: string;
  card_type: string;
  card_expiry: string;
  is_default: boolean;
}

export interface TierOption {
  value: string;
  label: string;
  fee: string;
  first_visit_free: boolean;
}

export interface CardStepData {
  has_card: boolean;
  cards: SavedCard[];
  service_tier: string;
  service_tier_default: string;
  tier_options: TierOption[];
  nothing_charged_today: string;
  first_delivery_notice: string;
}

export interface PropertyData {
  drop_location?: '' | 'front_door' | 'back_door' | 'garage' | 'other';
  garage_code?: string;
  gate_location?: string;
  gate_code?: string;
  latch_notes?: string;
  dog_present?: boolean;
  dog_loose?: boolean;
  dog_notes?: string;
  feed_storage_location?: string;
  feeder_location?: string;
  waterer_location?: string;
  grit_location?: string;
  shell_location?: string;
  notes?: string;
  captured?: boolean;
}

// The customer's delivery address - the ONE source of truth for where a subscriber
// lives (the Bridge Club profile reads it from here). A change re-geocodes and
// re-checks the service area on the server.
export interface DeliveryAddress {
  service_address: string;
  city: string;
  state: string;
  postal_code: string;
  needs_verification: boolean;
  can_change: boolean;
  change_blocked_reason: string | null;
}

export interface DeliveryAddressUpdateResult extends DeliveryAddress {
  message: string;
}

export interface PropertyPhoto {
  id: number;
  kind: string;
  caption: string;
  url: string;
  source: 'customer' | 'walk' | 'driver';
  // "Added by your driver" / "Added by Red Bridge" / '' for the customer's own.
  label: string;
  captured_at: string | null;
}

export type SubscriptionState =
  // 'awaiting_launch': set up and waiting for the launch date (delivery/launch.py).
  // 'awaiting_activation': live, set up, not yet in an activated batch (delivery/activation.py).
  | 'setup_incomplete' | 'awaiting_launch' | 'awaiting_activation' | 'open' | 'locked' | 'waiting' | 'today'
  | 'en_route' | 'delivered' | 'payment_failed' | 'paused' | 'cancelled';

export interface SubscriptionCard {
  state: SubscriptionState;
  status: string;
  // Set before the launch date: what the customer is told instead of a delivery date.
  launch_line?: string | null;
  // Set while the whole service is paused by a manager (delivery/pause.py); gone on resume.
  service_pause_line?: string | null;
  tier: string | null;
  tier_label: string;
  preferred_day: string | null;
  next_step_label: string | null;
  card: { last_four: string; card_type: string; expiry: string; expired: boolean } | null;
  order: {
    id: number;
    delivery_date: string;
    status: string;
    window: { opens: string; last_day: string; open: boolean; reason: string };
    lines: { id: number; label: string; bag_count: number }[];
    total: string | null;
    skip_reason: string;
  } | null;
  stop: { delivery_date: string; status: string; completed_at: string | null } | null;
  tracking: null;
  phone: string;
}

export type RideAlongCadence = 'next' | 'every' | 'every_2' | 'every_3';
export interface RideAlong {
  id: number;
  product: { id: number; brand: string; display_name: string; full_name: string; image_url: string; weight_lbs: string | null };
  bag_count: number; cadence: RideAlongCadence; cadence_label: string;
  price: string | null; on_open_order: boolean; available: boolean;
}
export interface DeliveryPageLine {
  id: number; label: string; bag_count: number; unit_price: string | null; line_total: string | null;
  bag_lbs: string | null; milled: boolean; bin_id: number | null; bin_name: string | null;
}
export interface DeliveryPageBin {
  bin_id: number; name: string; feed: string | null; feed_category?: string; form_type?: string | null;
  image_url?: string;
  // What the last receipt put in the bin (the Feed page's name) - can differ from `feed`.
  last_added: string | null; feed_differs: boolean;
  current_lbs: string | null; daily_lbs: string | null;
  days_to_due: string | null; bag_lbs: string | null; price: string | null; selected: boolean;
  deliverable: boolean; milled: boolean; animals: number; reason: string | null;
}
export interface DeliveryPage extends Omit<SubscriptionCard, 'order'> {
  order: (Omit<NonNullable<SubscriptionCard['order']>, 'lines'> & {
    lines: DeliveryPageLine[];
    subtotal: string | null; tax: string | null; fee: string | null;
    estimated_total: string | null; charged_total: string | null;
    charge_date: string; charged: boolean;
    addable_bins: { bin_id: number; name: string; feed: string | null; bag_name: string | null; price: string | null; days_to_due: string | null }[];
  }) | null;
  locked_line?: string;
  bins: DeliveryPageBin[];
  // "Also bring": what rides along with the feed and lives in no bin.
  ride_alongs: RideAlong[];
  preferences: {
    preferred_day: string | null; day_choices: string[]; tier: string | null;
    drop_location: '' | 'front_door' | 'back_door' | 'garage' | 'other';
    garage_code_set: boolean;
    card: SubscriptionCard['card'];
  };
  loyalty: { progress: number; bags_until_reward: number; reward_available: boolean };
  history: { id: number; delivery_date: string; delivered_at: string | null; lines: { label: string; bag_count: number }[]; total: string | null }[];
}

// Discovery + self-serve entry (delivery/signup_views.py). A member with no subscription
// asks what is available where they live. The ZIP lists decide WORDING only - every member
// is captured in StoreOpeningInterest regardless: 'available' is a live delivery ZIP,
// 'opening' is the Caldwell store, 'growing' is everywhere else (with an opt-in).
export interface DeliveryAvailability {
  status: 'subscriber' | 'available' | 'opening' | 'growing';
  // Set while the whole service is paused (delivery/pause.py). Signups stay open.
  service_pause_line?: string | null;
  postal_code?: string;
  prefill?: { city: string; state: string; postal_code: string };
  opening_message?: string;
  message?: string;
  opted_in?: boolean;
  copy?: TierCopy;
  ask?: string;
  // The street address registration already collected, so they confirm rather than retype.
  pending_address?: { service_address: string; city: string; state: string; postal_code: string } | null;
}

export interface SubscribeResult {
  subscribed: boolean;
  already?: boolean;
  subscriber_id?: number;
  next_step?: string | null;
  needs_verification?: boolean;
  // Out of area: kept on the Caldwell list and told plainly, never a bare refusal.
  code?: 'not_in_area' | 'cancelled' | 'missing';
  captured?: boolean;
  message?: string;
  opening_message?: string;
}

// The copy for one tier, written server-side (delivery/opening.py) so the registration
// flow and the dashboard card say the same thing and a wording change is one edit.
export interface TierCopy {
  heading: string;
  product: string;
  paragraphs: string[];
  wholesale: {
    question: string;
    text: string;
    email: string;
    phone: string;
    phone_href: string;
  };
}

// What to say about a ZIP, for a flow with no member cookie (delivery/public_views.py).
export interface ZipTier {
  status: 'available' | 'opening' | 'growing';
  postal_code: string;
  copy: TierCopy;
  ask?: string;
  // Set while the whole service is paused (delivery/pause.py). Signups stay open.
  service_pause_line?: string | null;
}

export const deliveryAPI = {
  getZipTier: (zip: string): Promise<ZipTier> =>
    fetch(`${PUBLIC}/zip-tier/?zip=${encodeURIComponent(zip)}`, { headers: JSON_HEADERS }).then(handle),

  // The opening-alert consent, which cannot ride along on the (portal-locked)
  // registration payload.
  recordOpeningInterest: (body: {
    email: string; zip_code: string; city?: string; state?: string;
    first_name?: string; last_name?: string; opt_in?: boolean; source?: string;
  }): Promise<{ captured: boolean }> =>
    fetch(`${PUBLIC}/opening-interest/`, {
      method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body),
    }).then(handle),

  // A delivery-ZIP registrant's street address. No account exists yet, so it is recorded
  // as a lead; the subscription itself is one tap on the card once they are signed in.
  recordDeliveryAddress: (body: {
    name: string; email: string; phone?: string; address_line1: string;
    city: string; state: string; zip_code: string; source?: string;
  }): Promise<{ success: boolean; in_service_area: boolean }> =>
    fetch(`${PUBLIC}/waitlist/`, {
      method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body),
    }).then(handle),

  // "Want to hear when we reach your area?" - consent on a row that already exists.
  optInToOpeningAlerts: (): Promise<{ opted_in: boolean; message: string }> =>
    fetch(`${BASE}/opening-alerts/`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
    }).then(handle),

  getAvailability: (): Promise<DeliveryAvailability> =>
    fetch(`${BASE}/availability/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  subscribe: (address: { service_address: string; city: string; state: string; postal_code: string }):
    Promise<SubscribeResult> =>
    fetch(`${BASE}/subscribe/`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(address),
    }).then(handle),

  getOnboarding: (): Promise<OnboardingState> =>
    fetch(`${BASE}/onboarding/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  getBinsPricing: (): Promise<BinsPricing> =>
    fetch(`${BASE}/bins-pricing/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  // The landing page's session id, handed back once after registration so the visit
  // becomes a signup on the Landing tracker (delivery/landing_tracker.py).
  attributeLanding: (sid: string): Promise<{ attributed: number }> =>
    fetch(`${BASE}/me/landing-attribution/`, { method: 'POST', headers: JSON_HEADERS, credentials: 'include',
           body: JSON.stringify({ sid }) }).then(handle),
  // "Yes, tell me if that changes" from a customer whose only bins are pet food.
  notePetOnlyInterest: (): Promise<{ noted: boolean; interest_at: string | null; message: string }> =>
    fetch(`${BASE}/me/pet-only-interest/`, { method: 'POST', headers: JSON_HEADERS, credentials: 'include' })
      .then(handle),
  setPreferences: (binIds: number[], preferredDay: string): Promise<OnboardingState> =>
    fetch(`${BASE}/delivery-preferences/`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ bin_ids: binIds, preferred_day: preferredDay }),
    }).then(handle),

  getProperty: (): Promise<PropertyData> =>
    fetch(`${BASE}/property/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  saveProperty: (data: PropertyData): Promise<PropertyData> =>
    fetch(`${BASE}/property/`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(handle),

  // Multipart — do NOT set Content-Type; the browser adds the multipart boundary.
  uploadPhoto: (kind: string, image: File, caption = ''): Promise<any> => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('image', image);
    if (caption) fd.append('caption', caption);
    return fetch(`${BASE}/property/photos/`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    }).then(handle);
  },

  skipProperty: (): Promise<OnboardingState> =>
    fetch(`${BASE}/property/skip/`, { method: 'POST', headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  // Step 5 (the card). getCard reads the member's saved cards + tier + copy WITHOUT the
  // Scan & Go local_access gate; patchCard sets the service tier and records consent. The
  // card itself is vaulted through the existing bridgeClubAPI HelcimPay flow; the FK is
  // linked to the subscription at complete().
  getCard: (): Promise<CardStepData> =>
    fetch(`${BASE}/card/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  patchCard: (body: { service_tier?: string; consent?: boolean }): Promise<CardStepData> =>
    fetch(`${BASE}/card/`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(body),
    }).then(handle),

  // Scan a bag against the 1,060-product delivery catalog. NOT the POS
  // endpoint: that one is staff-gated and searches StoreProduct, where most of
  // the catalog has no row at all.
  scanBarcode: (barcode: string): Promise<ScanResult> =>
    fetch(`${BASE}/scan/${encodeURIComponent(barcode)}/`, {
      headers: JSON_HEADERS, credentials: 'include',
    }).then(handle),

  // Typing reaches the same 1,060 catalog rows scanning does. Before this the
  // picker searched only the 46 hand-entered FeedProduct rows, so a bag you
  // could SCAN could not be FOUND by name.
  searchCatalog: (q: string, species?: string, opts?: { rideAlong?: boolean }): Promise<{
    count: number; query: string; results: ScannedProduct[];
    // Set when the server cut the list (150 rows); total is what matched.
    total?: number; truncated?: boolean; limit?: number;
  }> => {
    const qs = new URLSearchParams({ q });
    if (species) qs.set('species', species);
    // Ride-along mode: browse a species without typing; treats reachable (no bin).
    if (opts?.rideAlong) qs.set('ride_along', '1');
    return fetch(`${BASE}/catalog-search/?${qs.toString()}`, {
      headers: JSON_HEADERS, credentials: 'include',
    }).then(handle);
  },

  // Called at BIN-SAVE, never at selection: mints the FeedProduct, promotes to
  // a StoreProduct and maps them, then reports whether the result can actually
  // be delivered. Returns 200 even when it cannot - the customer keeps the bin.
  adoptCatalogProduct: (productId: number): Promise<{
    feed_product_id: number | null;
    store_product_id: number | null;
    deliverable: boolean;
    reason: string | null;
    message: string;
    product: ScannedProduct;
  }> =>
    fetch(`${BASE}/catalog-adopt/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify({ product_id: productId }),
    }).then(handle),

  // "We don't carry this - here's what it is." Sourcing intelligence.
  reportScan: (body: {
    barcode: string; customer_note?: string; brand_hint?: string;
  }): Promise<{ reported: boolean; created: boolean; message: string }> =>
    fetch(`${BASE}/scan/report/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify(body),
    }).then(handle),

  complete: (): Promise<OnboardingState> =>
    fetch(`${BASE}/complete/`, { method: 'POST', headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  // Reference photos of the customer's property (who added each), and removing one.
  getPropertyPhotos: (): Promise<PropertyPhoto[]> =>
    fetch(`${BASE}/property/`, { headers: JSON_HEADERS, credentials: 'include' })
      .then(handle).then((d: { photos?: PropertyPhoto[] }) => d.photos || []),

  deletePropertyPhoto: (photoId: number): Promise<null> =>
    fetch(`${BASE}/property/photos/${photoId}/`, {
      method: 'DELETE', headers: JSON_HEADERS, credentials: 'include',
    }).then(handle),

  // The subscription home card (delivery/subscription_card.py). 404 = not a subscriber.
  getSubscription: (): Promise<SubscriptionCard> =>
    fetch(`${BASE}/subscription/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  // The customer's own change / skip, inside the change window. Errors are { error }.
  setOrderBagCount: (orderId: number, lineId: number, bagCount: number): Promise<SubscriptionCard> =>
    fetch(`${BASE}/orders/${orderId}/lines/${lineId}/`, {
      method: 'PATCH', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify({ bag_count: bagCount }),
    }).then(handle),

  skipOrder: (orderId: number): Promise<SubscriptionCard> =>
    fetch(`${BASE}/orders/${orderId}/skip/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include',
    }).then(handle),

  // The Delivery page behind the dashboard card (delivery/subscription_page.py). Its
  // payload is a superset of the card's, and every change endpoint answers with it.
  getDeliveryPage: (): Promise<DeliveryPage> =>
    fetch(`${BASE}/delivery/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),
  // "Also bring": ride-alongs (delivery/ride_along.py). Each answers with the Delivery page.
  listRideAlongs: (): Promise<{ ride_alongs: RideAlong[] }> =>
    fetch(`${BASE}/ride-alongs/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),
  addRideAlong: (body: { product_id: number; bag_count: number; cadence: RideAlongCadence }): Promise<DeliveryPage> =>
    fetch(`${BASE}/ride-alongs/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include', body: JSON.stringify(body),
    }).then(handle),
  setRideAlong: (id: number, body: { bag_count?: number; cadence?: RideAlongCadence }): Promise<DeliveryPage> =>
    fetch(`${BASE}/ride-alongs/${id}/`, {
      method: 'PATCH', headers: JSON_HEADERS, credentials: 'include', body: JSON.stringify(body),
    }).then(handle),
  stopRideAlong: (id: number): Promise<DeliveryPage> =>
    fetch(`${BASE}/ride-alongs/${id}/`, { method: 'DELETE', headers: JSON_HEADERS, credentials: 'include' }).then(handle),
  addOrderLine: (orderId: number, binId: number): Promise<DeliveryPage> =>
    fetch(`${BASE}/orders/${orderId}/lines/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify({ bin_id: binId }),
    }).then(handle),
  // The picker's price and "more about this feed" for the FeedProducts it shows,
  // for the form (pellet/mash) the customer has picked.
  feedInfo: (ids: number[], form?: string): Promise<Record<string, FeedInfo>> => {
    const qs = new URLSearchParams({ ids: ids.join(',') });
    if (form) qs.set('form', form);
    return fetch(`${BASE}/feed-info/?${qs.toString()}`, { credentials: 'include' }).then(handle);
  },
  // "From the next delivery, bring this instead." No feed is added to the bin.
  changeBinFeed: (
    binId: number, body: { catalog_product_id?: number; feed_product_id?: number; form_type?: string },
  ): Promise<DeliveryPage & { notice?: string | null }> =>
    fetch(`${BASE}/bins/${binId}/feed/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify(body),
    }).then(handle),
  setDeliveryDay: (preferredDay: string): Promise<DeliveryPage> =>
    fetch(`${BASE}/delivery-day/`, {
      method: 'PATCH', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify({ preferred_day: preferredDay }),
    }).then(handle),

  // The single-use card link staff email after a property walk. The token is the
  // credential; redeeming verifies the email and sets the Bridge Club cookies.
  redeemCardLink: (token: string): Promise<{ next: string; first_name: string; has_password: boolean }> =>
    fetch(`${API_BASE_URL}/delivery/card-link/redeem/`, {
      method: 'POST', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify({ token }),
    }).then(handle),

  // The delivery address - the one place a subscriber's location lives. A change is
  // re-geocoded and re-checked against the service ZIPs server-side; an out-of-area
  // ZIP rejects with { error, code: 'out_of_area' } and nothing is saved.
  getAddress: (): Promise<DeliveryAddress> =>
    fetch(`${BASE}/address/`, { headers: JSON_HEADERS, credentials: 'include' }).then(handle),

  updateAddress: (body: {
    service_address: string; city: string; state: string; postal_code: string;
  }): Promise<DeliveryAddressUpdateResult> =>
    fetch(`${BASE}/address/`, {
      method: 'PATCH', headers: JSON_HEADERS, credentials: 'include',
      body: JSON.stringify(body),
    }).then(handle),
};
